import type { RecipientProvider } from '../inbox/providers';
import { MIN_RATE_SAMPLE, MIN_SEGMENT_SAMPLE } from './metrics';
import {
  addDays,
  daysBetween,
  daysInMonth,
  previousRange,
  type DatePreset,
  type DayRange,
} from './periods';
import { rate, twoProportionTest } from './stats';

/**
 * The Home page (Step 16b): the period it compares with, and its plain sentences. Rules pick
 * every fact and number; "Use AI" may only reword the summary (acceptRewording). Browser-safe.
 */
export const HOME_PRESETS = ['this_month', 'last_month', '7d', '30d', 'custom'] as const;
export type HomePreset = (typeof HOME_PRESETS)[number];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const int = (n: number) => Math.round(n).toLocaleString('en-US');

/** "1 Oct" (a workspace day, 'YYYY-MM-DD'). */
export const shortDate = (day: string): string =>
  `${Number(day.slice(8, 10))} ${MONTHS[Number(day.slice(5, 7)) - 1] ?? ''}`;

/** The first day of the month before `day`'s month. */
const monthBefore = (day: string): string => {
  const y = Number(day.slice(0, 4));
  const m = Number(day.slice(5, 7));
  return m === 1 ? `${y - 1}-12-01` : `${y}-${String(m - 1).padStart(2, '0')}-01`;
};

/** The same days, `months` earlier (cut at the end of that shorter period). */
function sameDaysEarlier(range: DayRange, months: number, periodMonths: number): DayRange {
  const [y, m] = range.from.split('-').map(Number) as [number, number];
  const start = new Date(Date.UTC(y, m - 1 - months, 1));
  const from = start.toISOString().slice(0, 10);
  const end = new Date(Date.UTC(y, m - 1 - months + periodMonths, 0)).toISOString().slice(0, 10);
  const to = addDays(from, daysBetween(range.from, range.to) - 1);
  return { from, to: to < end ? to : end };
}

/**
 * What a period is compared with: "this month so far" with the same days of last month
 * (1–18 Oct with 1–18 Sep), "last month" with the month before, this week / quarter / year so
 * far with the same days of the one before, last week with the week before, any other range
 * with the same number of days just before it.
 */
export function comparisonRange(preset: DatePreset, range: DayRange): DayRange {
  if (preset === 'this_week' || preset === 'last_week')
    return { from: addDays(range.from, -7), to: addDays(range.to, -7) };
  if (preset === 'this_quarter') return sameDaysEarlier(range, 3, 3);
  if (preset === 'this_year') return sameDaysEarlier(range, 12, 12);
  if (preset === 'this_month' || preset === 'last_month') {
    const from = monthBefore(range.from);
    const last = addDays(from, daysInMonth(from) - 1);
    if (preset === 'last_month') return { from, to: last };
    const sameDays = addDays(from, daysBetween(range.from, range.to) - 1);
    return { from, to: sameDays < last ? sameDays : last };
  }
  return previousRange(range);
}

/** How a period and its comparison are said ("This month so far" … "at this point last month"). */
export function periodWords(
  preset: DatePreset,
  range: DayRange,
): { period: string; before: string } {
  switch (preset) {
    case 'today':
      return { period: 'Today', before: 'yesterday' };
    case 'yesterday':
      return { period: 'Yesterday', before: 'the day before' };
    case 'day_before_yesterday':
      return { period: 'The day before yesterday', before: 'the day before' };
    case 'this_week':
      return { period: 'This week so far', before: 'at this point last week' };
    case 'last_week':
      return { period: 'Last week', before: 'the week before' };
    case 'this_quarter':
      return { period: 'This quarter so far', before: 'at this point last quarter' };
    case 'this_year':
      return { period: 'This year so far', before: 'at this point last year' };
    case 'all':
      return { period: 'Since the start', before: '' };
    case 'this_month':
      return { period: 'This month so far', before: 'at this point last month' };
    case 'last_month':
      return { period: 'Last month', before: 'the month before' };
    case '7d':
      return { period: 'In the last 7 days', before: 'the 7 days before' };
    case '15d':
      return { period: 'In the last 15 days', before: 'the 15 days before' };
    case '30d':
      return { period: 'In the last 30 days', before: 'the 30 days before' };
    case '90d':
      return { period: 'In the last 90 days', before: 'the 90 days before' };
    case 'custom':
      return {
        period: `From ${shortDate(range.from)} to ${shortDate(range.to)}`,
        before: 'the same number of days before',
      };
  }
}

export type ChangeDirection = 'up' | 'down' | 'same';

/** "Up from 19 at this point last month" (null when there is nothing to compare with). */
export function changeText(
  now: number,
  before: number | null,
  words: string,
): { direction: ChangeDirection; text: string } | null {
  if (before === null) return null;
  if (now === before) return { direction: 'same', text: `Same as ${words}` };
  return {
    direction: now > before ? 'up' : 'down',
    text: `${now > before ? 'Up' : 'Down'} from ${int(before)} ${words}`,
  };
}

/** Email providers in plain words: the summary names a family, not a technical type. */
export const PROVIDER_FAMILY: Readonly<Record<RecipientProvider, string>> = {
  gmail: 'Gmail / Google',
  google_workspace: 'Gmail / Google',
  outlook: 'Outlook / Microsoft',
  microsoft_365: 'Outlook / Microsoft',
  yahoo: 'Yahoo',
  zoho: 'Zoho',
  other: 'other providers',
};

interface Ratio {
  /** People emailed. */
  n: number;
  /** People who replied. */
  x: number;
}

export interface HomeFacts {
  preset: DatePreset;
  range: DayRange;
  emailed: number;
  replied: number;
  before: { emailed: number; replied: number } | null;
  /** Replies now and before per provider family (the sentence names the one that moved most). */
  families?: { label: string; now: Ratio; before: Ratio }[];
}

const people = (n: number) => `${int(n)} ${n === 1 ? 'person' : 'people'}`;

/**
 * "This month so far: you emailed 4,210 people. 131 replied (up from 119 at this point last
 * month). The reply rate dropped a little, mainly at Outlook / Microsoft." (2026-10-27: replies
 * only, no interested / opportunities.)
 */
export function homeSummary(f: HomeFacts): string {
  const w = periodWords(f.preset, f.range);
  if (f.emailed === 0 && f.replied === 0) return `${w.period}: no campaign emails were sent yet.`;
  const change = f.before === null ? null : changeText(f.replied, f.before.replied, w.before);
  const changeWords = change ? ` (${change.text[0]?.toLowerCase()}${change.text.slice(1)})` : '';
  let line = `${w.period}: you emailed ${people(f.emailed)}. ${int(f.replied)} replied${changeWords}.`;
  const replies = replyChange(f);
  if (replies) line += ` ${replies}`;
  return line;
}

/** "The reply rate dropped a little, mainly at Outlook / Microsoft." (or nothing: no clear change). */
function replyChange(f: HomeFacts): string | null {
  if (!f.before || f.emailed < MIN_RATE_SAMPLE || f.before.emailed < MIN_RATE_SAMPLE) return null;
  const now = rate(f.replied, f.emailed);
  const was = rate(f.before.replied, f.before.emailed);
  if (now === null || was === null || was === 0) return null;
  const rel = (now - was) / was;
  if (Math.abs(rel) < 0.05) return null;
  const how =
    rel < 0 ? `dropped${rel > -0.2 ? ' a little' : ''}` : `went up${rel < 0.2 ? ' a little' : ''}`;
  const family = mainFamily(f.families ?? [], rel > 0 ? 1 : -1);
  return `The reply rate ${how}${family ? `, mainly at ${family}` : ''}.`;
}

/**
 * The family that moved the reply rate most in that direction (its share × its change), named
 * only when its own change is clear (two-proportion test, p < 0.05): a small group's chance
 * swing is never blamed.
 */
function mainFamily(families: NonNullable<HomeFacts['families']>, sign: 1 | -1): string | null {
  const total = families.reduce((s, d) => s + d.now.n, 0);
  let best: { label: string; impact: number } | null = null;
  for (const d of families) {
    if (d.now.n < MIN_SEGMENT_SAMPLE || d.before.n < MIN_SEGMENT_SAMPLE) continue;
    const now = rate(d.now.x, d.now.n);
    const was = rate(d.before.x, d.before.n);
    const test = twoProportionTest(d.now, d.before);
    if (now === null || was === null || total === 0 || !test || test.p >= 0.05) continue;
    const impact = (((now - was) * d.now.n) / total) * sign;
    if (impact > 0 && (!best || impact > best.impact)) best = { label: d.label, impact };
  }
  return best?.label ?? null;
}

/** The "Needs your attention" items, in order (who sees each is decided by the API). */
/**
 * "Needs your attention" (Home) and the top bar's "Things to fix" badge (Step 17c): one list,
 * so both always agree. In this order.
 */
export const ATTENTION_KINDS = [
  'reconnect_mailboxes',
  'paused_mailboxes',
  'dns_domains',
  'unknown_sends',
  'deliverability_alerts',
  'warming_mailboxes',
] as const;
export type AttentionKind = (typeof ATTENTION_KINDS)[number];

/** Listed for information, never counted on the badge (nothing to fix: warming up is expected). */
export const ATTENTION_INFO: ReadonlySet<AttentionKind> = new Set(['warming_mailboxes']);

/** The badge's number: the things a person must act on. */
export const attentionCount = (items: { kind: AttentionKind; count: number }[]): number =>
  items.reduce((n, i) => n + (ATTENTION_INFO.has(i.kind) ? 0 : i.count), 0);

const plural = (n: number, one: string, many: string) => `${int(n)} ${n === 1 ? one : many}`;

/** One line per item, in plain words, and what the button says. */
export function attentionText(kind: AttentionKind, n: number): { text: string; action: string } {
  switch (kind) {
    case 'reconnect_mailboxes':
      return {
        text: `${plural(n, 'mailbox needs', 'mailboxes need')} to be reconnected: ${n === 1 ? 'it cannot' : 'they cannot'} send or read replies`,
        action: 'Reconnect',
      };
    case 'deliverability_alerts':
      return {
        text: `${plural(n, 'deliverability problem is', 'deliverability problems are')} open (spam rate or test emails in spam)`,
        action: 'View',
      };
    case 'paused_mailboxes':
      return {
        text:
          n === 1 ? '1 mailbox is paused or blocked' : `${int(n)} mailboxes are paused or blocked`,
        action: 'Fix',
      };
    case 'warming_mailboxes':
      return {
        text: `${plural(n, 'mailbox is', 'mailboxes are')} still warming up (campaign emails go out all the same, within the mailbox limits)`,
        action: 'View',
      };
    case 'dns_domains':
      return {
        text: `${plural(n, 'domain cannot', 'domains cannot')} send: DNS records are missing`,
        action: 'Fix',
      };
    case 'unknown_sends':
      return {
        text: `${plural(n, 'email has', 'emails have')} an unknown result: check if ${n === 1 ? 'it was' : 'they were'} sent`,
        action: 'Check',
      };
  }
}

/** The Sales Rep Home: "This month so far: 12 new replies (up 3 from …)." */
export function repSummary(f: {
  preset: DatePreset;
  range: DayRange;
  created: number;
  before: number | null;
}): string {
  const w = periodWords(f.preset, f.range);
  const change = changeText(f.created, f.before, w.before);
  const changeWords = change ? ` (${change.text[0]?.toLowerCase()}${change.text.slice(1)})` : '';
  return `${w.period}: ${plural(f.created, 'new reply', 'new replies')}${changeWords}.`;
}
